import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Tonal } from "@/components/ui/tonal";

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  actions?: ReactNode;
  /** "default" for full-page empties, "compact" for inline/within-card empties */
  variant?: "default" | "compact";
  /** Semantic heading level for the title (defaults to "h2") */
  headingLevel?: "h2" | "h3" | "h4";
  /** When true, opt out of the Tonal frame (use when parent already provides framing) */
  unframed?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  variant = "default",
  headingLevel = "h2",
  unframed = false,
  className,
}: EmptyStateProps) {
  const Heading = headingLevel;
  const inner = (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        variant === "default" && "gap-3 sm:gap-4 py-4 sm:py-8",
        variant === "compact" && "gap-2.5 sm:gap-3 py-2 sm:py-4",
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            "text-muted-foreground",
            variant === "default" && "h-8 w-8",
            variant === "compact" && "h-6 w-6",
          )}
          aria-hidden="true"
        />
      )}
      <div className="space-y-2">
        <Heading
          className={cn(
            "font-semibold text-foreground",
            variant === "default" && "text-md",
            variant === "compact" && "text-base",
          )}
        >
          {title}
        </Heading>
        {description && (
          <p className="mx-auto max-w-[52ch] text-sm text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 pt-1">{actions}</div>}
    </div>
  );

  if (unframed) {
    return <div className={className}>{inner}</div>;
  }

  return (
    <Tonal padding={variant === "default" ? "lg" : "md"} className={className}>
      {inner}
    </Tonal>
  );
}
